import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiTrash2 } from 'react-icons/fi';
import { toast } from 'sonner';

const Cart = ({ updateCartCount }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}`);
      const data = await response.json();
      setCart(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load cart:', error);
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, variant, quantity) => {
    const sessionId = localStorage.getItem('session_id');
    const variantParam = variant ? `?variant=${encodeURIComponent(variant)}` : '';

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}/item/${productId}${variantParam}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity })
        }
      );

      if (response.ok) {
        await loadCart();
        updateCartCount();
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      toast.error('Failed to update quantity');
    }
  };

  const removeItem = async (productId, variant) => {
    const sessionId = localStorage.getItem('session_id');
    const variantParam = variant ? `?variant=${encodeURIComponent(variant)}` : '';

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}/item/${productId}${variantParam}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        await loadCart();
        updateCartCount();
        toast.success('Item removed from cart');
      }
    } catch (error) {
      console.error('Failed to remove item:', error);
      toast.error('Failed to remove item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="cart-loading">
        <div className="text-2xl font-['Fredoka'] text-[#FFB800]">Loading cart...</div>
      </div>
    );
  }

  return (
    <div className="content-wrapper py-12 px-4 sm:px-6 lg:px-8 min-h-screen" data-testid="cart-page">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl sm:text-6xl font-black font-['Fredoka'] text-[#3A1C00] text-center mb-12" data-testid="cart-title">
            Your Cart
          </h1>

          {cart.items.length === 0 ? (
            <div className="text-center py-24" data-testid="empty-cart-message">
              <p className="text-2xl text-[#7D5A35] font-['Fredoka'] mb-8">Your cart is empty</p>
              <button onClick={() => navigate('/products')} className="btn-primary" data-testid="empty-cart-shop-button">
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.items.map((item, index) => (
                <motion.div
                  key={`${item.product_id}-${item.variant || 'default'}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-3xl p-6 shadow-sm flex gap-6 items-center"
                  data-testid={`cart-item-${index}`}
                >
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-24 h-24 rounded-2xl object-cover"
                    data-testid={`cart-item-image-${index}`}
                  />

                  <div className="flex-1" data-testid={`cart-item-info-${index}`}>
                    <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00]" data-testid={`cart-item-name-${index}`}>
                      {item.name}
                    </h3>
                    {item.variant && (
                      <p className="text-[#FF6B9E] font-medium" data-testid={`cart-item-variant-${index}`}>{item.variant}</p>
                    )}
                    <p className="text-2xl font-bold font-['Fredoka'] text-[#FFB800] mt-2" data-testid={`cart-item-price-${index}`}>
                      ${item.price.toFixed(2)}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.variant, Math.max(1, item.quantity - 1))}
                      className="w-10 h-10 rounded-full bg-[#FFF1E5] text-[#3A1C00] font-bold hover:bg-[#FFB800] hover:text-white transition-all"
                      data-testid={`cart-item-decrease-${index}`}
                    >
                      -
                    </button>
                    <span className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] w-8 text-center" data-testid={`cart-item-quantity-${index}`}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.variant, item.quantity + 1)}
                      className="w-10 h-10 rounded-full bg-[#FFF1E5] text-[#3A1C00] font-bold hover:bg-[#FFB800] hover:text-white transition-all"
                      data-testid={`cart-item-increase-${index}`}
                    >
                      +
                    </button>
                  </div>

                  <button
                    onClick={() => removeItem(item.product_id, item.variant)}
                    className="text-[#FF3B30] hover:text-[#FF1F13] transition-colors p-2"
                    data-testid={`cart-item-remove-${index}`}
                  >
                    <FiTrash2 size={24} />
                  </button>
                </motion.div>
              ))}

              {/* Cart Summary */}
              <div className="bg-[#FFF1E5] rounded-3xl p-8 mt-8" data-testid="cart-summary">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00]">Total:</span>
                  <span className="text-4xl font-black font-['Fredoka'] text-[#FFB800]" data-testid="cart-total">
                    ${cart.total.toFixed(2)}
                  </span>
                </div>
                
                <button
                  onClick={() => navigate('/checkout')}
                  className="btn-primary w-full text-xl py-6"
                  data-testid="proceed-to-checkout-button"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Cart;